/**
 * @namespace client/access
 */
import Request from '~/utils/infra/client/request'
import { Maybe, always, assoc, curry, isEmpty, lensProp, map, over, pipe, pipe_, prop, reduce, renameKey_, when } from '~/utils/lib/core/prelude'

/**
 * @desc #PAR-1
 * @typedef {Record<Structure["id"], Structure>} Access_v3
 */

/**
 * @typedef {object} Structure
 * @property {string} uuidData PK
 * @property {string} id unique slug
 * @property {string} code_client FK, legacy `Client["code"]` PK
 * @property {Array<import('@ghsfr/acls-commons').ACLS>} acl
 * @property {import('~/stores/client/access').SOFTWARE_TYPE} logiciel
 * @property {string} raison
 * @property {Record<Company["code_societe"], Company>} societes
 */

/**
 * @typedef {object} Company
 * @property {string} societeId PK
 * @property {string} code_societe unique slug
 * @property {string} raison_sociale eg. "Animation Education Culture Loisirs (ECLAT)"
 * @property {string} nom_abrege eg. "ECLAT Anim"
 * @property {boolean} activation
 */

/**
 * @enum {typeof SOFTWARE_TYPE[keyof typeof SOFTWARE_TYPE]}
 * @readonly
 */
export const SOFTWARE_TYPE = /** @type {const} */ ({
  LA_PAIE: 'LaPaie',
  SPAIECTACLE: 'Spaiectacle',
})

export const ERRORS = {
  NO_STRUCTURE_FOUND: 'no structure found',
  NO_COMPANY_FOUND: 'no company found',
  ACCESS_NOT_GRANTED: 'access_not_granted',
}

export const useStore_client_access = defineStore('client/access', {
  state: () => ({
    /** @type {Access_v3} */
    // @ts-ignore: UNSAFE coerce (pseudo maybe monad)
    data: [],
  }),
  actions: {
    /// Actions
    ///
    /**
     * @function read
     * @return {Promise<Access_v3>}
     */
    read() {
      const route = 'v3/acls'

      const request = new Request({
        method: 'get',
        route
      })

      return request.authorize().exec()
        .then( parseFromRemoteToClient_v3 )
        .then( removeInvalidData )
    },
    /**
     * @function hydrate
     * @return {Promise<void>}
     */
    hydrate() {
      return this.read()
        .then(data => {
          this.$reset() // (mandatory: due to "pinia" shallow cloning)
          this.$patch({ data })
        })
    },
  }
})
/** @type {(data: any) => Access_v3} */
const parseFromRemoteToClient_v3 = data => map.record(
  pipe(
    renameKey_('uuid_data', 'uuidData'),
    over(
      // @ts-expect-error
      lensProp('societes'),
      when(
        isEmpty,
        always({}),
      )
    )
  ),
  data
)
/** @type {(data: Access_v3) => Access_v3} */
// @TODO: create a dedicated `filter.record`?
const removeInvalidData = data => reduce.record(
  (
    /** @type {Access_v3} */ acc,
    /** @type {Structure} */ structure,
    /** @type {Structure["id"]} */ id,
  ) => {
    return isEmpty(structure.acl)
      ? acc
      : assoc(id, structure, acc)
  },
  {},
  data
)

/**
 * @param {import('~/stores/client/access').SOFTWARE_TYPE} logiciel
 * @return {string}
 */
export function printSoftwareType(logiciel) {
  switch (logiciel) {
    case SOFTWARE_TYPE.LA_PAIE:     return `La Paie`
    case SOFTWARE_TYPE.SPAIECTACLE: return `sPAIEctacle`
  }
}
/**
 * @NOTE #SP-2278: `SOFTWARE_TYPE.LA_PAIE` can only have `CONTRACT_TYPE.CDD`
 * @param {import('~/stores/client/access').SOFTWARE_TYPE} logiciel
 * @return {boolean}
 */
export function hasContractTypeChoice(logiciel) {
  switch (logiciel) {
    case SOFTWARE_TYPE.LA_PAIE:     return false
    case SOFTWARE_TYPE.SPAIECTACLE: return true
  }
}
/**
 * @type {import('./index_').curry_findStructure}
 */
export const findStructure = curry((
  /** @type {Access_v3} */
  accessData,
  /** @type {Structure["id"]} */
  structure_id
) => pipe_(
    prop(structure_id, accessData),
    Maybe.toMaybe
  )
)
/**
 * @type {import('./index_').curry_findCompany}
 */
export const findCompany = curry((
  /** @type {Structure} */
  structure,
  /** @type {Company["code_societe"]} */
  companyCode
) => pipe_(
    prop(companyCode, structure.societes),
    Maybe.toMaybe
  )
)
